import type { FormikErrors, FormikHelpers } from 'formik';
import Cookies from 'js-cookie';
import cloneDeep from 'lodash/cloneDeep';
import now from 'lodash/now';
import { useLocale } from 'next-intl';
import { useReCaptcha } from 'next-recaptcha-v3';
import hash from 'object-hash';

import { asAbsoluteURL } from '@commons/helpers/url';
import { LOCALES } from '@commons/i18n';
import { API_WEBSITE } from '@commons/utils/constants';

// Defining and declaring the window object with the GMT `dataLayer` property.

type WindowWithDataLayer = Window & {
  dataLayer: Record<string, unknown>[];
};

declare const window: WindowWithDataLayer;

// Type definition for Drupal's form response.

type FormResponse<V> = {
  sid?: string;
  error?: FormikErrors<V>;
  message?: string;
  status?: number;
};

// Type definition for a Drupal form being submitted.

type WebformValues = {
  webform_id: string;
  [key: string]: string | File;
};

/**
 * Fetch a session token from Drupal.
 *
 * @returns A promise that resolves to a session token or null.
 */

const getToken = (): Promise<string | null> => {
  const url = asAbsoluteURL(`/session/token`);

  return fetch(url)
    .then((response) => response.text())
    .catch((err: Error) => {
      console.warn(
        'There is an error with the backend configuration. Unable to fetch session token.'
      );
      console.error(err);

      return null;
    });
};

/**
 * Generate a unique filename for a file field.
 *
 * @param file The file to generate a unique filename for.
 * @param field The name of the field the file is associated with.
 * @param prefix A prefix to add to the filename.
 *
 * @returns A unique filename for the file (prefix-field-timestamp-hash.extension).
 */

const getUniqueFilename = (file: File, field = 'file', prefix = '') => {
  const extension = file?.name.split('.').pop() || '';
  const name = [prefix, field, now(), hash(file)].join('-');

  return [name, extension].join('.');
};

// Type definition for the submitForm function options.

type SubmitFormOptions<V> = {
  /**
   * The Drupal webform ID to submit the form to.
   */
  webformId: string;

  /**
   * The Formik form values, typed generically.
   */
  values: V;

  /**
   * The current page locale (ensure Drupal validation strings can be translated).
   */
  locale?: string;

  /**
   * An optional URL to submit the form to instead of the default Drupal endpoint.
   */
  submitURL?: string;
};

/**
 * A function to handle form submission to Drupal with dynamic File Upload handling.
 *
 * Calls the Drupal back-end using fetch and returns the response promise.
 *
 * @param options - The options for the form submission.
 *
 * @returns A fetch promise.
 */

const submitForm = async <V>(options: SubmitFormOptions<V>): Promise<FormResponse<V>> => {
  const { webformId, values, locale = LOCALES.EN, submitURL } = options;

  if (typeof values !== 'object') return Promise.reject();

  if (typeof webformId === 'string' && webformId.length) {
    const webformValues: WebformValues = {
      webform_id: webformId,
      ...cloneDeep(values),
    };

    // Receive Token from Drupal for CSRF security.
    const token = await getToken();
    if (!token) return Promise.reject();

    // Uploads files one by one and replaces them with file IDs from Drupal.
    const uploadFiles = async () => {
      const country = webformValues['country'] || '';
      const fileEndpointPrefix = `/${locale}/webform_rest/${webformId}/upload`;

      const uploadFile = async (field: string, file: File) => {
        const filename = getUniqueFilename(file, field, country.toString());
        const url = asAbsoluteURL(`${fileEndpointPrefix}/${field}?_format=json`, {
          scheme: 'https',
        });

        try {
          const response = await fetch(url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/octet-stream',
              'Content-Disposition': `attachment; filename="${filename}"`,
              'X-CSRF-Token': token,
            },
            body: file,
          });

          if (!response.ok) {
            const errorText = await response.text();
            console.error(`Upload failed: ${response.status}`, errorText);
            throw new Error(`File upload failed with status ${response.status}: ${errorText}`);
          }

          const data = await response.json();

          // Check and log the uploaded file ID.
          if (data?.fid?.[0]?.value) {
            webformValues[field] = data.fid[0].value;
          } else {
            console.error(`Unexpected upload response format for "${field}"`, data);
            throw new Error('Invalid file upload response format.');
          }
        } catch (error) {
          console.error(`File upload failed for "${field}"`, error);
          throw error; // Propagate the error to stop the process.
        }
      };

      // Loop through form values and upload files.
      for (const [field, value] of Object.entries(webformValues)) {
        if (value instanceof File) {
          await uploadFile(field, value);
        }
      }
    };

    await uploadFiles();

    // Set the submission URL to the optional custom URL.
    let url = submitURL;

    // If undefined, fallback to the Drupal webform_rest endpoint.
    // `webform_id` in `webformValues` is what determines which Form is being submitted.
    if (!url) {
      url = asAbsoluteURL(`/${locale}/webform_rest/submit?_format=json&website=${API_WEBSITE}`, {
        scheme: 'https',
      });
    }

    // Submit the form data to Drupal.
    // On error, read status and pass error/validation messages back to form for user feedback.
    const response = fetch(url, {
      method: 'POST',
      headers: {
        'X-CSRF-Token': token,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(webformValues),
    })
      .then((data) => data.json())
      .catch(({ response: { data, status } }) => ({ ...data, status }));

    return response;
  }

  return Promise.reject();
};

// Type definition for generic form values.

type GenericFormValues = {
  form_type?: string;
};

// Type definition for the options of the `useFormSubmit` hook.

type UseFormSubmitOptions = {
  /**
   * The Drupal webform ID to submit the form to.
   */
  webformId: string;

  /**
   * A callback function to be called on successful form submission.
   */
  onSuccess?: () => void;

  /**
   * An optional URL to submit the form to instead of the default Drupal endpoint.
   */
  submitURL?: string;
};

/**
 * A hook to handle form submission with Formik, ReCaptcha and GTM.
 *
 * @param options - The options for the hook.
 *
 * @returns A function that can be passed to Formik's `onSubmit` prop.
 */

export const useFormSubmit = <V extends GenericFormValues>(options: UseFormSubmitOptions) => {
  const locale = useLocale();
  const { executeRecaptcha } = useReCaptcha();

  const { webformId, onSuccess, submitURL } = options;

  const handleSubmit = async (formValues: V, helpers: FormikHelpers<V>) => {
    const { setStatus, setErrors, setSubmitting } = helpers;

    if (!executeRecaptcha) {
      console.warn(
        'Submit Halted: ReCaptcha is empty. Please validate RECAPTCHA_KEY environment key/value is present.'
      );
      return;
    }

    const recaptcha = await executeRecaptcha('form');

    // Extract value from cookie visitor_id937003, if not present, leave empty.
    const pardotVisitorId = Cookies.get('visitor_id937003') || '';
    const values = { ...formValues, recaptcha, pardot_visitor_id: pardotVisitorId };

    return submitForm({ webformId, values, locale, submitURL })
      .then((response = {}) => {
        const { sid, error, message } = response;

        if (sid) {
          setStatus({ state: 'success' });

          if (onSuccess) onSuccess();

          // If the `dataLayer` property is present on the `window` object, push an event to it.
          if (window && window.dataLayer) {
            window.dataLayer.push({
              event: 'form_submitted',
              formId: webformId,
              form_type: values.form_type || 'N/A',
            });
          }
        }

        if (error) {
          setStatus({ state: 'error', msg: message });
          setErrors(error);
        }

        setSubmitting(false);
      })
      .catch((err) => {
        console.error(`SubmitForm Error${err !== undefined ? ':' : ''}`, err || '');
        setStatus({ state: 'error' });
        setSubmitting(false);
      });
  };

  return handleSubmit;
};
